import React, { useState } from 'react';
import { Tabs, Card } from 'antd';
import {
  LineChartOutlined,
  BarChartOutlined,
  DollarOutlined,
  RiseOutlined,
  LockOutlined,
  CrownOutlined
} from '@ant-design/icons';
import DiscountRevenueScatter from './DiscountRevenueScatter';
import CouponAverageComparison from './CouponAverageComparison';
import DiscountRateUsage from './DiscountRateUsage';
import CouponVsRegularComparison from './CouponVsRegularComparison';

const { TabPane } = Tabs;

const isUnlocked = true;

const RevenueAnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('scatter');

  if (!isUnlocked) {
    return (
      <div className="p-6">
        {/* Suspension Notice */}
        <div style={{
          border: '2px solid #dc2626',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '16px',
          backgroundColor: '#fef2f2'
        }}>
          <h3 style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '15px', marginBottom: '8px' }}>
            機能の一時停止について
          </h3>
          <p style={{ color: '#991b1b', fontSize: '13px', margin: 0, lineHeight: 1.7 }}>
            本機能は一度デモ動作いたしましたが、確認したところ一部タブにおいて不具合が見つかりました。
            当初の実装予定範囲外であったため、エラー修正および本番運用に耐えうる品質での再構築には
            <strong>プレミアムオプション契約</strong>が必要となります。
            ご興味のある方は開発担当までお問い合わせください。
          </p>
        </div>

        {/* Premium Overlay */}
        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderRadius: '16px',
          padding: '48px 32px',
          color: 'white',
          textAlign: 'center',
          marginBottom: '24px',
          boxShadow: '0 20px 60px rgba(245, 87, 108, 0.3)'
        }}>
          <CrownOutlined style={{ fontSize: '64px', marginBottom: '16px', color: '#ffd700' }} />
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', color: 'white' }}>
            クーポン収益分析
          </h1>
          <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '32px' }}>
            クーポンの収益性・費用対効果を多角的に分析できるプレミアム機能です
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {[
              { icon: <LineChartOutlined />, title: '値引きvs売上', desc: '値引き額と売上の相関分析' },
              { icon: <BarChartOutlined />, title: 'クーポン別比較', desc: 'クーポン種別の平均客単価比較' },
              { icon: <DollarOutlined />, title: '値引き率別分析', desc: '値引き率帯ごとの利用傾向' },
              { icon: <RiseOutlined />, title: '効果測定', desc: 'クーポン利用時vs通常時の比較' },
            ].map((item, idx) => (
              <div key={idx} style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '12px',
                padding: '16px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>{item.icon}</div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{item.title}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Blurred Preview */}
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px' }}>
          <div style={{
            filter: 'blur(8px)',
            pointerEvents: 'none',
            opacity: 0.5
          }}>
            <Card className="shadow-lg">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  💰 クーポン収益分析
                </h1>
                <p className="text-gray-600">
                  クーポンの収益性・費用対効果を多角的に分析できます
                </p>
              </div>
              <div style={{ height: '300px', background: '#f5f5f5', borderRadius: '8px' }} />
            </Card>
          </div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <LockOutlined style={{ fontSize: '48px', color: '#f5576c' }} />
            <p style={{ color: '#f5576c', fontWeight: 'bold', marginTop: '8px' }}>プレミアム機能</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card className="shadow-lg">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            💰 クーポン収益分析（Phase 1B）
          </h1>
          <p className="text-gray-600">
            クーポンの収益性・費用対効果を多角的に分析できます
          </p>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" type="card">
          <TabPane
            tab={
              <span>
                <LineChartOutlined />
                値引きvs売上
              </span>
            }
            key="scatter"
          >
            <DiscountRevenueScatter />
          </TabPane>

          <TabPane
            tab={
              <span>
                <BarChartOutlined />
                クーポン別比較
              </span>
            }
            key="average"
          >
            <CouponAverageComparison />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DollarOutlined />
                値引き率別分析
              </span>
            }
            key="rate"
          >
            <DiscountRateUsage />
          </TabPane>

          <TabPane
            tab={
              <span>
                <RiseOutlined />
                効果測定
              </span>
            }
            key="comparison"
          >
            <CouponVsRegularComparison />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default RevenueAnalyticsDashboard;
